/**
 * Cross-store benchmarks + true ROAS.
 *
 * Two ideas the merchant can't get anywhere else:
 *
 *  1. Percentile ranking against comparable stores. Nobody else has the
 *     cross-store data to compute it.
 *  2. Incremental vs reported ROAS. Last-touch attribution systematically
 *     over-credits paid channels; geo holdouts measure what would have
 *     happened anyway. Showing both, honestly, is the differentiator —
 *     even though the honest number is the less flattering one.
 */
import { useEffect, useState } from 'react';
import { api, fmt, type Benchmark } from '../api';

const LOWER_IS_BETTER = new Set(['cpa']);

const METRIC_LABEL: Record<string, string> = {
  ctr: 'Click-through rate',
  roas: 'Return on ad spend',
  cvr: 'Conversion rate',
  cpa: 'Cost per acquisition',
};

export default function Benchmarks() {
  const [rows, setRows] = useState<Benchmark[]>([]);
  const [truth, setTruth] = useState<Awaited<ReturnType<typeof api.incrementalityOverview>> | null>(null);
  const [pixel, setPixel] = useState<Awaited<ReturnType<typeof api.pixelRecovery>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([api.benchmarks(), api.incrementalityOverview(), api.pixelRecovery()])
      .then(([b, t, p]) => {
        if (b.status === 'fulfilled') setRows(b.value);
        if (t.status === 'fulfilled') setTruth(t.value);
        if (p.status === 'fulfilled') setPixel(p.value);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <p className="eyebrow">Tier 3 · Intelligence</p>
      <h1 className="pg-title">Benchmarks &amp; true ROAS</h1>
      <p className="pg-sub">
        How you compare to similar stores, and what your ads are actually driving once you subtract
        the sales that would have happened anyway.
      </p>

      {loading && <div className="empty">Loading…</div>}

      {!loading && truth && truth.campaigns.length > 0 && (
        <div className="card card-p truth-card">
          <h4>
            Reported vs incremental ROAS
            <span className="meta">geo holdout tests</span>
          </h4>
          <div className="truth-grid">
            <div className="metric">
              <div className="lbl">Blended multiplier</div>
              <div className="val">{(truth.blended_multiplier * 100).toFixed(0)}%</div>
              <div className="delta flat">of reported ROAS is incremental</div>
            </div>
            {truth.campaigns.slice(0, 3).map((c) => (
              <div key={c.campaign_id} className="metric">
                <div className="lbl">Campaign {c.campaign_id.slice(0, 8)}</div>
                <div className="val">{c.true_roas.toFixed(2)}×</div>
                <div className="delta dn">reported {c.reported_roas.toFixed(2)}×</div>
              </div>
            ))}
          </div>
          <p className="footnote">
            The gap is not an error. Last-touch attribution credits paid channels for sales that
            would have arrived anyway — the holdout measures the difference.
          </p>
        </div>
      )}

      {!loading && pixel && (
        <div className="card card-p">
          <h4>
            First-party pixel recovery <span className="meta">last 30 days</span>
          </h4>
          <div className="grid-3">
            <div className="metric">
              <div className="lbl">Purchases seen</div>
              <div className="val">{fmt.num(pixel.totalPurchases)}</div>
            </div>
            <div className="metric">
              <div className="lbl">Visible to Meta directly</div>
              <div className="val">{fmt.num(pixel.metaDirect)}</div>
            </div>
            <div className="metric">
              <div className="lbl">Recovered via CAPI</div>
              <div className="val">{pixel.recoveryRatePct.toFixed(0)}%</div>
              <div className="delta up">{fmt.num(pixel.recoveredViaCapi)} conversions</div>
            </div>
          </div>
        </div>
      )}

      <div className="card card-p">
        <h4>
          Your percentile
          {rows[0] && <span className="meta">{rows[0].cohort} · n={fmt.num(rows[0].sampleSize)}</span>}
        </h4>

        {rows.length === 0 && !loading ? (
          <div className="empty">
            Benchmarks need a daily cross-store snapshot. On a fresh install this fills in after the
            first nightly rollup.
          </div>
        ) : (
          <div className="bench-grid">
            {rows.map((b) => (
              <BenchmarkCard key={b.metric} b={b} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function BenchmarkCard({ b }: { b: Benchmark }) {
  const lowerBetter = LOWER_IS_BETTER.has(b.metric);
  const strong = b.yourPercentile >= 60;
  const format = (v: number) =>
    b.metric === 'cpa' ? fmt.money(v) : b.metric === 'roas' ? `${v.toFixed(2)}×` : fmt.pct(v, 2);

  return (
    <div className="card card-p bench-card">
      <div className="bench-label">{METRIC_LABEL[b.metric] ?? b.metric}</div>
      <div className="bench-value">{format(b.yourValue)}</div>

      <div className="percentile-track">
        <span style={{ width: `${Math.min(100, Math.max(2, b.yourPercentile))}%` }} />
      </div>

      <div className={`bench-percentile ${strong ? 'strong' : ''}`}>
        {b.yourPercentile}
        <sup>{ordinal(b.yourPercentile)}</sup> percentile
      </div>
      <div className="bench-median">
        cohort median {format(b.median)}
        {lowerBetter && <span className="footnote"> · lower is better</span>}
      </div>
    </div>
  );
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] ?? s[v] ?? s[0];
}
